import { CloseIcon } from "components/atoms/Icon";
import Button from "components/atoms/buttons/Button";
import Modal from "components/atoms/modal";
import { useState } from "react";
import { toast } from "react-toastify";
import ErrorMessage from "components/atoms/forms/ErrorMessage";
import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useLoaderContext } from "components/context/LoaderContext";
import { post } from "utils/request";
import { createFormDataFromJSON } from "utils/HelperFunctions";
import { useDataProviderContext } from "pages/MagnetDetails/DataProvider";
import { getUpdateExclusiveMembersRoute } from "utils/helpers";
import { TOAST_CONFIG } from "../../../../constants";

interface IProps {
  open: boolean;
  onClose: () => void;
  onSubmit: any;
}
const EmptyFileList = new DataTransfer().files;

function UploadCSVModalForExclusiveMembers({ open = false, onClose, onSubmit }: IProps) {
  const [file, setFile] = useState<FileList>(EmptyFileList);
  const [errorMessage, setErrorMessage] = useState("");
  const { setLoading } = useLoaderContext();
  const { magnetDetailsState } = useDataProviderContext();

  const handleSetFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage("");
    const { files } = e.target;

    if (!files?.length || !files) {
      return toast.error("No file was selected..", TOAST_CONFIG);
    }
    if (files[0].type !== "text/csv") {
      setFile(EmptyFileList);
      setErrorMessage("Select a CSV file.");
      return null;
    }
    setFile(files);
    return null;
  };

  const handleConfirm = async () => {
    const payload = {
      members_csv: file[0],
    };

    setLoading(true);
    post(
      getUpdateExclusiveMembersRoute(magnetDetailsState?.brand_id as string, magnetDetailsState?.id as string),
      createFormDataFromJSON(payload),
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        processData: () => {
          if (onSubmit) {
            onSubmit();
          }
          onClose();
          setLoading(false);
          toast.success("CSV Uploaded Successfull!", TOAST_CONFIG);
        },
        processError: (err) => {
          setErrorMessage(err);
          setLoading(false);
          toast.error("Failed to add members!", TOAST_CONFIG);
        },
      },
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="relative rounded-2xl bg-primary p-8 text-white shadow-modal md:w-[600px]">
        <div className="flex justify-between">
          <div className="w-[95%]">
            <h1 className="font-satoshi text-2xl font-bold">Upload CSV</h1>
          </div>

          <button type="button" onClick={onClose} className="p-3">
            <CloseIcon className="-m-4 h-4 w-4 text-white" />
          </button>
        </div>
        <section className="mt-2 w-full font-satoshi text-sm font-450 leading-snug text-white text-opacity-70">
          Please upload a CSV file containing list of members&apos; emails and their statuses (&lsquo;active&rsquo; or
          &lsquo;inactive&rsquo;). Uploading a CSV file will append new members and overwrite existing member statuses
          based on the provided data.
        </section>
        <section className=" mb-3 mt-1">
          <a
            href="https://magnetiq-static.s3.amazonaws.com/media/public/efb9869823394973bf074ded7c09c93f.csv"
            target="_blank"
            rel="noreferrer"
            className="font-satoshi text-cyan-500 underline hover:text-cyan-300"
          >
            Sample CSV Template
          </a>
        </section>
        <label
          htmlFor="members"
          className="-mb-3 flex w-full cursor-pointer justify-between rounded-ll border-1 border-white bg-primary p-3.5 font-satoshi text-white"
        >
          {file.length === 0
            ? "Click to select CSV file containing emails of Whitelisted Users"
            : `Selected file: ${file[0].name}`}
          <input
            id="members"
            accept=".csv, .CSV"
            onChange={handleSetFile}
            autoComplete="off"
            type="file"
            className="hidden"
          />
        </label>
        <div className="relative -top-6 left-[calc(100%-2rem)] cursor-pointer">
          {file.length === 0 ? (
            <ArrowUpTrayIcon className="pointer-events-none h-5" />
          ) : (
            <XMarkIcon
              className="h-5"
              onClick={() => {
                setFile(EmptyFileList);
              }}
            />
          )}
        </div>
        <ErrorMessage errorMessage={errorMessage} />

        <div className="mt-3 flex justify-end gap-3">
          <Button variant="whiteOutline" text="CANCEL" onClick={onClose} additionalClasses="text-xs" />
          <Button
            disabled={file.length === 0}
            variant="magnetiqSolid"
            text="CONFIRM"
            onClick={handleConfirm}
            additionalClasses="text-xs"
          />
        </div>
      </div>
    </Modal>
  );
}

export default UploadCSVModalForExclusiveMembers;
